'use client';

import { createContext, useContext, useRef, type RefObject } from 'react';

/**
 * Context for providing a portal container element that lives inside the Theme wrapper.
 *
 * This solves the issue where Radix UI portals render to document.body, which is
 * outside the Theme component tree. By rendering portals inside a container that's
 * within the Theme, CSS variables cascade correctly to portal content.
 *
 * @see https://github.com/radix-ui/primitives/discussions/2862
 * @see https://github.com/radix-ui/themes/issues/135
 */
const PortalContainerContext =
  createContext<RefObject<HTMLDivElement | null> | null>(null);

export const usePortalContainer = () => {
  const containerRef = useContext(PortalContainerContext);
  // Return the current element or undefined (which falls back to document.body in Radix)
  return containerRef?.current ?? undefined;
};

interface PortalContainerProviderProps {
  children: React.ReactNode;
}

/**
 * Provides a portal container that should be placed inside a Theme wrapper.
 * This ensures all portals inherit CSS variables from the Theme.
 *
 * Usage:
 * ```tsx
 * <Theme style={{ '--primary-color': '#E31837' }}>
 *   <PortalContainerProvider>
 *     <YourContent />
 *     {/* Portals will render here, inheriting Theme variables *\/}
 *   </PortalContainerProvider>
 * </Theme>
 * ```
 */
export const PortalContainerProvider = ({
  children,
}: PortalContainerProviderProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <PortalContainerContext.Provider value={containerRef}>
      {children}
      {/* This div is the portal container - portals will render here instead of document.body */}
      <div ref={containerRef} id="portal-container" />
    </PortalContainerContext.Provider>
  );
};
